// Dependencies
import { useContext } from "react";

// Types
import { Types, Components } from '../types';

// Components
import Price from "./Price";
import CartIcon from "./CartIcon";
import { MinicartContext } from "../contexts/Minicart";
import { getBreakpoint } from "../utils/helpers";
import styled, { css } from "styled-components";
import { Helpers } from "../utils";


// NOTE: styles.borderWidth * 16 is temp as this wrapper has font-size: 12.75
const CartHeaderWrapper = styled.button<any>(({ loading, theme: { general: { transition, ...general }, components: { cartButton: styles } } }) => css`
	border: unset;
	outlined: unset;
	background: transparent;
		
	${['outlined', 'filled'].includes(styles.variant) &&
		`border: ${styles.borderWidth * 16}em ${styles.borderColor} solid`
	}
		
	${['filled'].includes(styles.variant) &&
		`background-color: ${styles.background};`
	}
		
	color: ${styles.textColor};

	font-family: inherit;
	border-radius: ${Helpers.getFormattedValue(styles.borderRadius)};
	
	width: fit-content;
	width: intrinsic;
	height: ${styles.size * 16}px;

	display: flex;
	align-items: center;
	
		${styles.subtotal.displayMode !== 'none'
		? `padding: ${styles.paddingBlock * 16}px ${styles.paddingInline * 16}px;`
			: `padding: 0 0.4rem`
		}
		
	transition: all ${transition.duration} ${transition.timingFunction};
		
		.wooptima-price {
				font-size: 0em;
				transition: all ${transition.duration} ${transition.timingFunction};
		color: ${styles.subtotal.textColor};
	}
		
		${styles.subtotal.displayMode === 'hover' ? `&:hover` : '&'} {
			& .wooptima-price {
					font-size: ${styles.subtotal.textSize}em;
			}
		}
		
		.wooptima-cart-header__icon {
				
				${styles.subtotal.displayMode !== 'none' && `
					&.left ~ .wooptima-price {
							${!loading && `margin-left: ${0.625}em;`}
					}
					&.right ~ .wooptima-price {
							${!loading && `margin-right: ${0.625}em;`}
					}
				`}
				
				margin-top: 0.25em;
				
				&--quantity {
		
		position: absolute;
		top: -0.5em;
		right: -1em;
		
		display: flex;
		align-items: center;
		justify-content: center;
		
		width: 1.5em;
		height: 1.5em;

		padding: 0.25em;
		border-radius: 50%;

		background: ${styles.quantity.background};
		color: ${styles.quantity.textColor};

		> span {
			font-size: ${styles.quantity.textSize}em;
			&.enter {
				font-size: 0em;
				overflow: hidden;
			}
			
			&.enter-active {
				font-size: ${styles.quantity.textSize}em;
				transition: all 400ms ease-in-out;
			}
			
			&.exit-active {
				font-size: 0em;
				transition: all 400ms ease-in-out;
			}

			&.loading-spinner {
	
				@keyframes spinner-rotate {
					from {
						transform: rotate(0deg);
						-webkit-transform: rotate(0deg);
					}
					to {
						transform: rotate(359deg);
						-webkit-transform: rotate(359deg);
					}
				}
				
				width: 100%;
				height: 100%;
	
				transform: scale(1);
				transform-origin: center;
				position: absolute;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: transform .4s cubic-bezier(0.04, 0.24, 0.37, 1.46) .3s;
			
				&.enter {
					transform: scale(0);
					overflow: hidden;
				}
				
				&.enter-active {
					transform: scale(1);
					transition: all 400ms ease-in-out;
				}
				
				&.exit-active {
					transform: scale(0);
					transition: all 400ms ease-in-out;
				}
				
				&:before {
					content: '';
					width: 1em;
					height: 1em;
					display: inline-block;
					border-radius: 50%;
					border: .25em solid #fff !important;
					border-left-color: rgba(255, 255, 255, 0.5) !important;
					animation: spinner-rotate 450ms infinite linear;
				}
	
			}

		}

		}

	svg {
		@media (min-width: 768px) {
		}
		width: 2.4rem;
		height: 2.4rem;
		height: ${styles.size - (styles.paddingInline * 1.325)}em;
		color: ${styles.icon.fill};
	}

	&:not(:disabled) {
		cursor: pointer;
		&:hover,
		&:focus {
					color: ${styles.hoverTextColor ?? styles.textColor};
					${['filled'].includes(styles.variant) &&
						`background-color: ${styles.hoverBackground ?? styles.background};`
					}
					${['outlined', 'filled'].includes(styles.variant) &&
						`border-color: ${styles.hoverBorderColor ?? styles.borderColor};`
					}
		}
	}

		
	@media screen and (max-width: ${getBreakpoint('tablet', 'sm')}) {
		z-index: 1; // For tooltips content stacking context
		padding: 0;
		justify-content: center;
		svg {
			height: ${styles.size / 1.2}em;
		}
	}

`)


const CartHeader = ({ icon, className }: Components.CartHeader) => {
	const { loading, updating, opened, itemsCount, itemsSubtotal, handleMinicartOpen, config, translations, theme } = useContext(MinicartContext);

	const cartIconProps = {
		className: `${theme.components.cartButton.icon.position} ${className}__icon`,
		icon,
		quantity: itemsCount,
		loading, config, translations, theme
	};

	return (
		<CartHeaderWrapper tabIndex={0} loading={loading} disabled={loading || updating || opened} className={className} onClick={handleMinicartOpen}>
			{theme.components.cartButton.icon.position === 'left' && <CartIcon {...cartIconProps} />}
			{theme.components.cartButton.subtotal.displayMode !== 'none' &&
				<Price
					value={itemsSubtotal}
					{...{ loading }}
					{...{ config, translations, theme }}
				/>
			}
			{theme.components.cartButton.icon.position === 'right' && <CartIcon {...cartIconProps} />}
		</CartHeaderWrapper>
	)

}

export default CartHeader; 